import classNames from 'classnames';
import { useContext } from 'react';

import { ButtonVariant, Icon, LinkAsButton, Typography } from '@/shared/ui';

import { trackPayButtonClick } from '@/loader/trackPayButtonClick';

import { ROUTES_BY_SERVICE_CODE } from '@/router/config';

import { getPublicRouteUrl } from '@/helpers/route';

import AuthContext from '@/contexts/Auth/AuthContext';

import { SERVICE_CODES } from '@/lib/consts';

import { AdaptiveImage } from '../AdaptiveImage/AdaptiveImage';

import type { CmsSlide } from '@/api/types';
import type { ImgPath } from '@/shared/ui';
import type { JSX } from 'react';

import './CmsBanner.scss';

const DEFAULT_BANNER_IMG = '/images/service-without-application-banner-bg.svg' as ImgPath;
const DEFAULT_TITLE = 'Сплачуйте будь-яку послугу МВС онлайн';
const DEFAULT_DESCRIPTION =
  'Виберіть послугу, підрозділ НПУ та вкажіть дані особи, яка буде замовляти послугу, та оплачуйте карткою на цьому сайті';
const DEFAULT_LINK = ROUTES_BY_SERVICE_CODE[SERVICE_CODES.PAYMENT_WITHOUT_APPLICATION];

interface Props {
  className?: string;
  banner?: CmsSlide | null;
}

export const CmsBanner: React.FC<Props> = ({ className, banner = null }): JSX.Element => {
  const { isLoggedIn } = useContext(AuthContext);

  const header = banner?.header ?? DEFAULT_TITLE;
  const text = banner?.text ?? DEFAULT_DESCRIPTION;
  const link = banner?.link ?? DEFAULT_LINK;
  const img = banner?.img ?? { desktop: DEFAULT_BANNER_IMG, mobile: DEFAULT_BANNER_IMG };
  const textColor = banner?.text_color ?? 'white';
  const resolvedLink = !banner || banner.external ? link : getPublicRouteUrl(link, isLoggedIn);

  return (
    <section className={classNames('cms-banner', className)}>
      <div className="container">
        <div className="cms-banner__wrapper-inner">
          <div className="cms-banner__image">
            <AdaptiveImage desktopSrc={img.desktop} mobileSrc={img.mobile} alt={header} />
          </div>
          <div className="cms-banner__content">
            <Typography as="h2" variant="H1" className="cms-banner__title" color={textColor}>
              {header}
            </Typography>
            <Typography variant="H3" normal className="cms-banner__description" color={textColor}>
              {text}
            </Typography>
          </div>

          <LinkAsButton
            className="cms-banner__btn"
            variant={ButtonVariant.CONTRAST}
            to={resolvedLink}
            target={banner?.external ? '_blank' : '_self'}
            rel={banner?.external ? 'noopener noreferrer nofollow' : undefined}
            onClick={(): void => trackPayButtonClick()}
          >
            Оплатити послугу <Icon name="arrow_forward" />
          </LinkAsButton>
        </div>
      </div>
    </section>
  );
};
